import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, Download,
  CreditCard, DollarSign, CheckCircle, Users, AlertCircle
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const GSTR2BPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [gstr2bData, setGstr2bData] = useState(null);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    supplier_gstin: '',
    supplier_name: '',
    invoice_no: '',
    invoice_value: { min: '', max: '' }
  });

  useEffect(() => {
    if (businessId) {
      fetchGSTR2B();
    }
  }, [month, year, businessId]);

  const fetchGSTR2B = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.gstr2b({ month, year });
      setGstr2bData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'gstr2b', format, month, year });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GSTR2B_${month.toString().padStart(2, '0')}_${year}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const summary = gstr2bData?.summary || {
    total_itc_available: 0,
    itc_from_b2b: 0,
    itc_from_imports: 0
  };

  const b2bInvoices = gstr2bData?.b2b_invoices || [];

  // Apply filters
  const filteredInvoices = b2bInvoices.filter(inv => {
    const matchesGstin = !columnFilters.supplier_gstin || 
      inv.supplier_gstin?.toLowerCase().includes(columnFilters.supplier_gstin.toLowerCase());
    const matchesName = !columnFilters.supplier_name || 
      inv.supplier_name?.toLowerCase().includes(columnFilters.supplier_name.toLowerCase());
    const matchesInvoice = !columnFilters.invoice_no || 
      inv.invoice_no?.toLowerCase().includes(columnFilters.invoice_no.toLowerCase());
    const matchesValue = (!columnFilters.invoice_value?.min || inv.invoice_value >= parseFloat(columnFilters.invoice_value.min)) &&
      (!columnFilters.invoice_value?.max || inv.invoice_value <= parseFloat(columnFilters.invoice_value.max));
    return matchesGstin && matchesName && matchesInvoice && matchesValue;
  });

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });

  return (
    <div className="h-full flex flex-col" data-testid="gstr2b-page">
      {/* Premium Header */}
      <ListPageHeader
        title="GSTR-2B – Auto-drafted ITC"
        stats={[
          { icon: CreditCard, label: 'Total ITC Available', value: formatCompactCurrency(summary.total_itc_available), color: 'emerald' },
          { icon: Users, label: 'ITC from B2B', value: formatCompactCurrency(summary.itc_from_b2b), color: 'blue' },
          { icon: DollarSign, label: 'ITC from Imports', value: formatCompactCurrency(summary.itc_from_imports), color: 'purple' },
        ]}
        secondaryAction={{
          label: monthLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[12px] font-medium text-blue-900">GSTR-2B Auto-Generated Report</p>
            <p className="text-[11px] text-blue-700 mt-0.5">This statement is auto-populated from your suppliers&apos; GSTR-1 filings. It shows ITC available for the selected period.</p>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading GSTR-2B data...</p>
              </div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No GSTR-2B data available</p>
              <p className="text-[13px] text-slate-400 mt-1">ITC from suppliers&apos; GSTR-1 will appear here</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Table Header */}
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '16%' }}>
                        <div className="flex items-center gap-1">
                          Supplier GSTIN
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.supplier_gstin}
                            onChange={(val) => setColumnFilters({...columnFilters, supplier_gstin: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '18%' }}>
                        <div className="flex items-center gap-1">
                          Supplier Name
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.supplier_name}
                            onChange={(val) => setColumnFilters({...columnFilters, supplier_name: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          Invoice No
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.invoice_no}
                            onChange={(val) => setColumnFilters({...columnFilters, invoice_no: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>Date</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '14%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Invoice Value (₹)
                          <ColumnFilter 
                            type="number" 
                            value={columnFilters.invoice_value}
                            onChange={(val) => setColumnFilters({...columnFilters, invoice_value: val})}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>Taxable (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>ITC Available (₹)</th>
                      <th className="tl-th tl-th-teal" style={{ width: '6%' }}>Status</th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Table Body */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody className="animate-stagger">
                    {filteredInvoices.map((inv, idx) => (
                      <tr 
                        key={idx} 
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                      >
                        <td className="tl-td" style={{ width: '16%' }}>
                          <span className="text-[11px] font-mono text-slate-600">{inv.supplier_gstin}</span>
                        </td>
                        <td className="tl-td tl-cell-regular" style={{ width: '18%' }}>{inv.supplier_name}</td>
                        <td className="tl-td tl-cell-primary" style={{ width: '12%' }}>{inv.invoice_no}</td>
                        <td className="tl-td tl-cell-regular" style={{ width: '10%' }}>
                          {inv.date ? new Date(inv.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                        </td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '14%' }}>₹{(inv.invoice_value || 0).toLocaleString()}</td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '12%' }}>₹{(inv.taxable || 0).toLocaleString()}</td>
                        <td className="tl-td text-right text-[13px] font-semibold text-emerald-600" style={{ width: '12%' }}>₹{(inv.itc_available || 0).toLocaleString()}</td>
                        <td className="tl-td" style={{ width: '6%' }}>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-medium">
                            <CheckCircle className="w-3 h-3" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredInvoices.length}</span> of <span className="font-medium text-slate-700">{b2bInvoices.length}</span> invoices
                </p>
                <p>
                  Total ITC Available: <span className="font-medium text-emerald-600">₹{summary.total_itc_available.toLocaleString()}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GSTR2BPage;
